import { Link, useNavigate } from 'react-router-dom';
import useQueryValues from '@/hooks/useQueryValues';
import usePaginationQuery from '@/hooks/usePaginationQuery';
import { useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useQuery } from '@tanstack/react-query';
import MainContentTitle from '@/components/molecule/MainContentTitle';
import { NAV_CONST } from '@/src/constants/nav';
import Filter from '@/components/organism/Filter';
import SectionCardNoneTitle from '@/components/molecule/SectionCardNoneTitle';
import Typo from '@/components/atom/Typo';
import PATH from '@/src/routes/path';
import Button from '@/components/atom/Button';
import LucideIcon from '@/components/atom/Icons/LucideIcon';
import Table from '@/components/atom/Table';
import { challengeListFilters } from '@/src/pages/Challenge/filter';
import { challengeListColumn } from '@/src/pages/Challenge/column';
import {
  getChallengesAPI,
  getChallengesExcelAPI,
} from '@/src/apis/rest/challenge';
import ExcelDownloadButton from '@/src/components/atom/ExcelDownloadButton';

function ChallengeListPage() {
  const navigate = useNavigate();

  const { defaultValues: defaultSearchParams } = useQueryValues({
    startedAt: ['startedAt', 'endedAt'],
  });
  const { page, pageSize, handlePageChange } = usePaginationQuery();

  const gridRef = useRef<AgGridReact>(null);
  const [searchParams, setSearchParams] = useState(defaultSearchParams);

  const {
    data: challengeListData,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ['challenge', searchParams, page, pageSize],
    queryFn: () => {
      return getChallengesAPI({
        ...searchParams,
        page,
        pageSize,
      });
    },
  });

  useEffect(() => {
    if (isFetching && gridRef.current) {
      gridRef.current.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  return (
    <>
      <MainContentTitle
        pageTitle={NAV_CONST.challengeManagement}
        breadcrumbList={[NAV_CONST.challengeManagement]}
      />
      <div className="mb-20 flex flex-col gap-6">
        <Filter
          title="검색 필터"
          filters={challengeListFilters()}
          onSubmit={values => {
            handlePageChange(1, pageSize, false);
            setSearchParams(values);
          }}
        />
        <div className="flex flex-col gap-y-6">
          <SectionCardNoneTitle
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <div className="flex items-center justify-between">
              <Typo typoType="b18m">챌린지 목록</Typo>
              <div className="flex gap-2">
                <ExcelDownloadButton
                  disabled={
                    isError || isFetching || !challengeListData?.rows?.length
                  }
                  fileName="챌린지_리스트"
                  downloadExcelAPI={getChallengesExcelAPI}
                  searchParams={searchParams}
                />
                <Link to={PATH.CHALLENGE_CREATE}>
                  <Button buttonSize="medium" onClick={() => {}}>
                    <div className="flex items-center gap-x-2">
                      <LucideIcon name="Plus" color="white" />
                      챌린지 등록
                    </div>
                  </Button>
                </Link>
              </div>
            </div>

            <Table
              ref={gridRef}
              rowData={isError ? [] : challengeListData?.rows || []}
              isPaginationDataMapping
              columnDefs={challengeListColumn(e => {
                navigate(PATH.CHALLENGE_DETAIL.replace(':id', String(e)));
              })}
              total={challengeListData?.count || 0}
              page={page}
              pageSize={pageSize}
              handlePaginationClick={handlePageChange}
            />
          </SectionCardNoneTitle>
        </div>
      </div>
    </>
  );
}

export default ChallengeListPage;
